import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { hotelInfo } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await axios.post(`${API}/contact`, formData);
      toast.success(result.data.message || 'Your inquiry has been sent successfully!');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(16, 40, 84, 0.7)' }} />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            We're here to help and answer any questions you might have
          </p>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#102854' }}>
                Get In Touch
              </h2>
              <p className="text-gray-600 mb-8">
                Have a question or need assistance? Our dedicated team is available 24/7 to help make your stay exceptional.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF3E2' }}>
                    <MapPin size={24} style={{ color: '#C9A961' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#102854' }}>Address</h3>
                    <p className="text-gray-600">{hotelInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF3E2' }}>
                    <Phone size={24} style={{ color: '#C9A961' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#102854' }}>Phone</h3>
                    <p className="text-gray-600">{hotelInfo.phone}</p>
                    <p className="text-sm text-gray-500">Available 24/7</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF3E2' }}>
                    <Mail size={24} style={{ color: '#C9A961' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#102854' }}>Email</h3>
                    <p className="text-gray-600">{hotelInfo.email}</p>
                    <p className="text-sm text-gray-500">Reservations: {hotelInfo.reservationsEmail}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF3E2' }}>
                    <Clock size={24} style={{ color: '#C9A961' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#102854' }}>Response Time</h3>
                    <p className="text-gray-600">Within 24 hours</p>
                    <p className="text-sm text-gray-500">Monday - Sunday</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white border-2 rounded-xl p-8 shadow-lg" style={{ borderColor: '#f3f4f6' }}>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#102854' }}>
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                      Name <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full"
                      style={{ borderColor: '#102854' }}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                      Email <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className="w-full"
                      style={{ borderColor: '#102854' }}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                      Phone
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      className="w-full"
                      style={{ borderColor: '#102854' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                      Message <span style={{ color: '#C9A961' }}>*</span>
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your inquiry..."
                      rows={5}
                      className="w-full"
                      style={{ borderColor: '#102854' }}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full font-semibold text-base py-6 transition-all hover:scale-[1.02]"
                    style={{
                      backgroundColor: '#C9A961',
                      color: '#ffffff'
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 px-4" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#102854' }}>
              Find Us
            </h2>
            <p className="text-gray-600">
              Conveniently located in the heart of the city
            </p>
          </div>
          <div className="w-full h-96 rounded-2xl overflow-hidden shadow-xl">
            <iframe
              title="Hotel Location Map"
              src={hotelInfo.mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
};